/**
 * What the Library board, Arrange and the Lab board agree on about a score from the library.
 *
 * A library score reaches Arrange by its id in the address, `/arrange?score=<id>`, rather
 * than as text carried in the navigation. The largest orchestral movement runs to tens of
 * megabytes, more than a browser will keep in a history entry, and an address can be
 * reloaded, bookmarked and sent to someone else where a navigation's state cannot. The id is
 * also what the run is started with: the server reads the score from its own disk, and the
 * text fetched here is only for drawing the original.
 */

import { useEffect, useRef, useState } from "react";
import type { LibraryEntry } from "@app/shared-types";
import { api } from "../api/client";

/** The query parameter Arrange reads a library score's id from. */
export const SCORE_PARAM = "score";

/** Where a library score is opened for arranging. */
export function arrangeHref(id: string): string {
  return `/arrange?${new URLSearchParams({ [SCORE_PARAM]: id }).toString()}`;
}

/** The collections whose entries are movements of the work their title names. */
const MOVEMENTS: ReadonlySet<string> = new Set(["orchestra", "quartets"]);

/** The score's number in its work: a movement's, or a song's in its set. */
function numbered(entry: LibraryEntry): string | null {
  // An orchestral or quartet score is one movement of the work its title names; a song is a
  // number in the set its work names, when it belongs to one.
  if (entry.movement === null) return null;
  return MOVEMENTS.has(entry.collection) ? `movement ${entry.movement}` : `No. ${entry.movement}`;
}

/** Work and movement, where the score belongs to a larger work. */
export function placing(entry: LibraryEntry): string | null {
  const number = numbered(entry);
  if (entry.work === null) return number;
  return number === null ? entry.work : `${entry.work}, ${number}`;
}

/**
 * What a score is called in the list under its work, which the row above already names: a
 * movement by its number, since its title is the work's, and any other score by its title
 * with its number in the work, if it has one.
 */
export function withinWork(entry: LibraryEntry): { title: string; where: string | null } {
  const number = numbered(entry);
  if (number !== null && MOVEMENTS.has(entry.collection)) {
    return { title: `Movement ${entry.movement}`, where: null };
  }
  return { title: entry.title, where: number };
}

/** The collection whose scores are dated by a song's first release, not a printed edition. */
const LEAD_SHEETS = "ewld";

/** The year the score was kept by, and what that year is of. */
export function dated(entry: LibraryEntry): string {
  return `${entry.collection === LEAD_SHEETS ? "first released" : "published"} ${entry.published}`;
}

/** The collection taken out of a data set's archive, each score matched to a work on IMSLP. */
const DATA_SET = "pdmx";

/** The collections nobody checked against a printed edition: a row of theirs says so. */
const UNCHECKED: ReadonlySet<string> = new Set([LEAD_SHEETS, DATA_SET]);

/** Who transcribed the score, where it was not a collection's own editors. */
export function transcribed(entry: LibraryEntry): string | null {
  return UNCHECKED.has(entry.collection) ? "community transcription" : null;
}

/** The collection of songs, each a score of its own. */
const SONGS = "lieder";

/** What kind of score of its work an entry is, as a row counts them. */
function kind(entry: LibraryEntry): string {
  if (entry.collection === DATA_SET) return "community transcription";
  if (entry.collection === LEAD_SHEETS) return "lead sheet";
  if (entry.movement !== null && MOVEMENTS.has(entry.collection)) return "movement";
  return entry.collection === SONGS ? "song" : "score";
}

/** What a work's scores are, counted by kind in the order they are listed. */
export function inside(scores: readonly LibraryEntry[]): string {
  const counted = new Map<string, number>();
  for (const entry of scores) counted.set(kind(entry), (counted.get(kind(entry)) ?? 0) + 1);
  return [...counted].map(([name, held]) => `${held} ${name}${held === 1 ? "" : "s"}`).join(" · ");
}

/** What the link to where the score was taken from is called. */
export function sourceLabel(entry: LibraryEntry): string {
  return entry.collection === DATA_SET ? "Data set" : "Source file";
}

/** What the link to the score's printed source is called: an edition, or only the work's page. */
export function editionLabel(entry: LibraryEntry): string {
  return entry.collection === DATA_SET ? "Work on IMSLP" : "Printed edition";
}

/** The name the score goes by on Arrange, and in the file names of what is saved from it. */
export function fileName(entry: LibraryEntry): string {
  const where = MOVEMENTS.has(entry.collection) && entry.movement ? ` ${entry.movement}` : "";
  const plain = `${entry.composer} - ${entry.title}${where}`.replace(/[\\/:*?"<>|]+/g, " ");
  return `${plain.replace(/\s+/g, " ").trim()}.musicxml`;
}

export interface Fetched {
  readonly entry: LibraryEntry;
  readonly text: string;
}

/**
 * Fetch the library score named by `id`, and hand it to `onFetched` once it has arrived.
 *
 * A reply that lands after the id has changed is dropped, so a reader who opens one score and
 * then another is never shown the first under the second's name.
 */
export function useLibraryScore(
  id: string | null,
  onFetched: (fetched: Fetched) => void,
): { loading: boolean; error: string | null } {
  const latest = useRef(onFetched);
  latest.current = onFetched;
  const [state, setState] = useState<{ loading: boolean; error: string | null }>({
    loading: false,
    error: null,
  });

  useEffect(() => {
    if (id === null) {
      setState({ loading: false, error: null });
      return undefined;
    }
    let live = true;
    setState({ loading: true, error: null });
    api.libraryScore(id).then(
      (payload) => {
        if (!live) return;
        setState({ loading: false, error: null });
        latest.current({ entry: payload.entry, text: payload.musicxml });
      },
      (cause: Error) => {
        if (live) setState({ loading: false, error: cause.message });
      },
    );
    return () => {
      live = false;
    };
  }, [id]);

  return state;
}
